import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react'
import { destroyBrowserCookie } from '@moonpig/web-core-browser-storage'
import {
  LastLoginMethod,
  LastLoginCookieData,
  setLastLoginMethod,
  clearLastLoginLocalStorage,
} from './lastLoginStorage'
import { LAST_LOGIN_COOKIE } from './cookies'

type LastLoginContextValue = {
  lastLogin: LastLoginCookieData | null
  isInitialized: boolean
  hasLastLogin: boolean
  setLastLogin: (data: LastLoginCookieData | null) => void
  setInitialized: () => void
  setLoginMethod: (method: LastLoginMethod) => void
  clearLastLogin: () => void
}

const LastLoginContext = createContext<LastLoginContextValue | null>(null)

export const LastLoginProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [lastLogin, setLastLoginState] = useState<LastLoginCookieData | null>(
    null,
  )
  const [isInitialized, setIsInitialized] = useState(false)

  const hasLastLogin = useMemo(() => {
    return lastLogin !== null
  }, [lastLogin])

  const setLastLogin = useCallback((data: LastLoginCookieData | null) => {
    setLastLoginState(data)
  }, [])

  const setInitialized = useCallback(() => {
    setIsInitialized(true)
  }, [])

  const setLoginMethod = useCallback((method: LastLoginMethod) => {
    setLastLoginMethod(method)
  }, [])

  const clearLastLogin = useCallback(() => {
    clearLastLoginLocalStorage()
    destroyBrowserCookie(LAST_LOGIN_COOKIE, { path: '/' })
    setLastLoginState(null)
  }, [])

  const contextValue = useMemo(
    () => ({
      lastLogin,
      isInitialized,
      hasLastLogin,
      setLastLogin,
      setInitialized,
      setLoginMethod,
      clearLastLogin,
    }),
    [
      lastLogin,
      isInitialized,
      hasLastLogin,
      setLastLogin,
      setInitialized,
      setLoginMethod,
      clearLastLogin,
    ],
  )

  return (
    <LastLoginContext.Provider value={contextValue}>
      {children}
    </LastLoginContext.Provider>
  )
}

export const useLastLogin = () => useContext(LastLoginContext)
